import axios from "axios";
import config from "../config";
import { translateApiError } from "./errorMessages";

// default
axios.defaults.baseURL = config.API_URL;

// content type
axios.defaults.headers.post["Content-Type"] = "application/json";

/**
 * 陣列查詢參數的序列化（票 21）。
 *
 * axios 1.x 的預設會把 `{ riskTypes: ["EOS","CVE"] }` 序列化成
 * `riskTypes[]=EOS&riskTypes[]=CVE`（帶方括號），而 ASP.NET Core 的預設模型繫結
 * **收不到那個 key** —— `string[] riskTypes` 會是空的。
 *
 * 後果不是錯誤，是**安靜地變成「沒有條件」**：使用者按下篩選，回來的是全部資料，
 * 看起來像「這個條件沒有生效」或「篩了跟沒篩一樣」，沒有任何訊息可查。
 * 這與側欄 `system_name` 是同一型的失效方式（ADR-0004）。
 *
 * 後端收得到的是**重複 key**：`riskTypes=EOS&riskTypes=CVE`。
 * 放在全域是因為之後每一支帶陣列參數的端點都會踩到（軟體 GetAll 同一組參數）——
 * 讓下一個人不必再發現一次。純量參數的行為不變。
 */
axios.defaults.paramsSerializer = (params) => {
  const sp = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) value.forEach((v) => v != null && sp.append(key, v));
    else sp.append(key, value);
  });
  return sp.toString();
};

// intercepting to capture errors
axios.interceptors.response.use(
  function (response) {
    // 回傳本體;數字與布林要照回 —— 用 truthy 判斷的話,`0`(例:DcMessage/GetUnreadCount 沒有未讀、
    // MarkAllAsRead 清了 0 筆)與 `false` 會變成整個 axios response 物件,呼叫端拿到的是物件不是值。
    // 空字串／null／undefined(無 body 的 200)維持舊行為回 response。
    const d = response.data;
    return d || typeof d === "number" || typeof d === "boolean" ? d : response;
  },
  function (error) {
    // 401:session 失效 → 發事件(不 import sessionStore,避免循環依賴),由 App 監聽登出導回。
    // 例外:登入嘗試本身的 401(GetToken 帳密錯誤)不是 session 失效,留在登入頁顯示錯誤。
    if (error && error.response && error.response.status === 401 && typeof window !== "undefined") {
      const reqUrl = (error.config && error.config.url) || "";
      if (!reqUrl.includes("/Authenticate/GetToken")) {
        window.dispatchEvent(new CustomEvent("auth:unauthorized"));
      }
    }
    // 2xx 以外進入這裡。原始錯誤保留給開發者除錯,使用者只看轉譯後的安全文案。
    if (process.env.NODE_ENV !== "test") {
      // eslint-disable-next-line no-console
      console.error(
        "[API error]",
        error && error.response ? error.response.status : "(no response)",
        error && error.response ? error.response.data : error && error.message
      );
    }
    return Promise.reject(translateApiError(error));
  }
);

/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token) => {
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;
};

class APIClient {
  /**
   * Fetches data from given url
   */
  get = (url, params) => {
    return axios.get(url, params);
  };

  /**
   * post given data to url
   */
  create = (url, data) => {
    return axios.post(url, data);
  };

  /**
   * 上傳檔案(multipart/form-data)。`data` 是 FormData;瀏覽器會自己補 boundary,
   * 這裡只把全域預設的 application/json 蓋掉。`params` 走 query string。
   */
  upload = (url, formData, params) => {
    return axios.post(url, formData, {
      params,
      headers: { "Content-Type": "multipart/form-data" },
      // DcImport 的 400 body 是 string(swagger),內容是給人看的原因 → 讓 translateApiError 採用
      trustStringError: true,
    });
  };

  /**
   * Updates data
   */
  update = (url, data) => {
    return axios.put(url, data);
  };

  /**
   * Delete
   */
  delete = (url, config) => {
    return axios.delete(url, { ...config });
  };
}

const getLoggedinUser = () => {
  const user = localStorage.getItem("authUser");
  if (!user) {
    return null;
  } else {
    return JSON.parse(user);
  }
};

export { APIClient, setAuthorization, getLoggedinUser };
